/**
 * Whether a keystroke aimed at `target` is text: the element owns a caret
 * (input, textarea, contenteditable) or reads letters as selection (select).
 * A bare-key hotkey must never fire here — the character belongs in the
 * value.
 */
export function isTextEditingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
}

/**
 * A bare letter is the most dangerous kind of hotkey: every text field, every
 * menu typeahead and every search box is competing for it. True whenever the
 * keystroke belongs to something that consumes letters — a text-editing
 * target, or a widget whose typeahead / own fields claim them: a menu,
 * listbox or combobox uses letters to jump, and a dialog may hold a field
 * that has not taken focus yet. Page-level hotkeys gate on this; a hotkey
 * scoped INSIDE a dialog gates on `isTextEditingTarget` instead.
 */
export function typingSomewhere(target: EventTarget | null): boolean {
  if (isTextEditingTarget(target)) return true;
  const el = target as HTMLElement | null;
  return (
    el?.closest('[role="menu"],[role="listbox"],[role="dialog"],[role="combobox"]') !=
    null
  );
}
